import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { TranslateModule } from "@ngx-translate/core";
import { MOBILE_MISSION_TASK_ROUTE_SEGMENTS } from "../../mission-task/mobile-mission-task-route.provider";
import { createMobileMissionAppAccess } from "../../mission-task/helpers/mobile-mission-app-access";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import { MobileRichTextEditorComponent } from "../../mission-task/mobile-rich-text-editor/mobile-rich-text-editor.component";
import { sanitizeVoiceReportHtml } from "../helpers/mobile-voice-report-html.util";
import type { IMobileVoiceReportTaskDraft } from "../models/mobile-voice-report.models";

type VoiceReportValidationTab = "report" | "tasks";

/** Composant générique : validation compte rendu (2 onglets, pattern signature). */
@Component({
  selector: "mobile-voice-report-validation",
  standalone: true,
  imports: [CommonModule, TranslateModule, MobileRichTextEditorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-voice-report-validation.component.html",
  styleUrl: "./mobile-voice-report-validation.component.scss",
})
export class MobileVoiceReportValidationComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly moduleAccess = inject(MobileModuleAccessService);

  private readonly missionNavSegments = inject(MOBILE_MISSION_TASK_ROUTE_SEGMENTS);

  readonly access = createMobileMissionAppAccess(this.moduleAccess, this.missionNavSegments).voiceReportAccess;

  readonly reportHtml = input.required<string>();
  readonly tasks = input.required<readonly IMobileVoiceReportTaskDraft[]>();
  readonly isSubmitting = input(false);

  readonly validated = output<{
    reportHtml: string;
    tasks: IMobileVoiceReportTaskDraft[];
  }>();

  protected readonly activeTab = signal<VoiceReportValidationTab>("report");
  protected readonly isEditingReport = signal(false);
  protected readonly editableReportHtml = signal("");
  protected readonly taskItems = signal<IMobileVoiceReportTaskDraft[]>([]);
  protected readonly showAddTask = signal(false);
  protected readonly newTaskLabel = signal("");
  protected readonly newTaskDescription = signal("");

  protected readonly tabs: readonly {
    readonly id: VoiceReportValidationTab;
    readonly labelKey: string;
    readonly count: () => number;
  }[] = [
    { id: "report", labelKey: "mobile.voiceReport.validation.tabs.report", count: () => 0 },
    {
      id: "tasks",
      labelKey: "mobile.voiceReport.validation.tabs.tasks",
      count: () => this.taskItems().length,
    },
  ];

  protected readonly safeReportHtml = computed<SafeHtml>(() => {
    const cleaned = sanitizeVoiceReportHtml(this.editableReportHtml());
    return this.sanitizer.bypassSecurityTrustHtml(cleaned);
  });

  constructor() {
    effect(() => {
      this.editableReportHtml.set(this.reportHtml());
      this.taskItems.set(this.tasks().map((task) => ({ ...task })));
    });
  }

  selectTab(id: VoiceReportValidationTab): void {
    this.activeTab.set(id);
  }

  startEditReport(): void {
    this.isEditingReport.set(true);
  }

  cancelEditReport(): void {
    this.editableReportHtml.set(this.reportHtml());
    this.isEditingReport.set(false);
  }

  saveEditReport(): void {
    this.isEditingReport.set(false);
  }

  onReportHtmlChange(html: string): void {
    this.editableReportHtml.set(html);
  }

  removeTask(index: number): void {
    this.taskItems.update((list) => list.filter((_, i) => i !== index));
  }

  openAddTask(): void {
    this.newTaskLabel.set("");
    this.newTaskDescription.set("");
    this.showAddTask.set(true);
  }

  cancelAddTask(): void {
    this.showAddTask.set(false);
  }

  confirmAddTask(): void {
    const label = this.newTaskLabel().trim();
    const description = this.newTaskDescription().trim();
    if (label.length < 2) {
      return;
    }
    this.taskItems.update((list) => [
      ...list,
      { Uid: `local-${Date.now()}`, Label: label, Description: description },
    ]);
    this.showAddTask.set(false);
  }

  submitValidation(): void {
    if (!this.access().hasUpdateAccess || this.isSubmitting()) {
      return;
    }
    this.validated.emit({
      reportHtml: sanitizeVoiceReportHtml(this.editableReportHtml()),
      tasks: this.taskItems(),
    });
  }

  trackTask(index: number, t: IMobileVoiceReportTaskDraft): string {
    return t.Uid ?? `task-${index}`;
  }
}
